import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomSheet, FloatingTabBar, NavItem } from '../design-system';
import type { FloatingTabBarItem, SidebarNavItem } from '../design-system';
import { AccountMenu } from './AccountMenu';
import { KeyboardCollapse } from './KeyboardCollapse';
import { NotificationsPopover } from './NotificationsPopover';
import { FaqModal } from '../FaqModal';
import { SupportModal } from '../SupportModal';
import { FeedbackPopover } from '../FeedbackPopover';
import { useAuth } from '../../hooks/useAuth';
import { useGuardedNavigation } from './unsavedGuard';
import { fetchHostConversations } from '../../lib/hostConversations';

type HostTab = 'trips' | 'chats' | 'library' | 'earnings' | 'more';

/** Host routes that get the bottom tab bar: the top-level screens, never a thread or the builder. */
const TAB_ROUTES = /^\/host(\/(chats|past|profile|availability|library|earnings))?\/?$/;

export function showsHostTabBar(pathname: string): boolean {
  return TAB_ROUTES.test(pathname) || pathname === '/settings';
}

export function hostTabFor(pathname: string): HostTab {
  // A trip's builder / preview belong to Trips.
  if (/^\/host\/?$/.test(pathname) || /^\/host\/(itinerary|preview)\//.test(pathname)) return 'trips';
  if (pathname.startsWith('/host/chats')) return 'chats';
  if (pathname.startsWith('/host/library')) return 'library';
  if (pathname.startsWith('/host/earnings')) return 'earnings';
  return 'more';
}

/**
 * Mobile nav for a signed-in host: Trips, Chats (dot when something is unread),
 * Library, Earnings and a "More" sheet with the rest (profile, availability,
 * past trips, help, role switch, sign out). Same floating pill as the traveler's.
 */
export function HostTabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const attempt = useGuardedNavigation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    if (!user) return;
    void fetchHostConversations(user.id).then((rows) => setHasUnread(rows.some((r) => r.unread)));
  }, [user, pathname]);

  const active = hostTabFor(pathname);
  const go = (to: string) => () => { if (pathname !== to) attempt(() => navigate(to)); };

  const tabItems: FloatingTabBarItem[] = [
    { key: 'trips', icon: 'layout-kanban', label: 'Trips', onClick: go('/host') },
    { key: 'chats', icon: 'message-circle', label: 'Chats', onClick: go('/host/chats'), dot: hasUnread },
    { key: 'library', icon: 'books', label: 'Library', onClick: go('/host/library') },
    { key: 'earnings', icon: 'coin', label: 'Earnings', onClick: go('/host/earnings') },
    { key: 'more', icon: 'dots', label: 'More', onClick: () => setMoreOpen(true) },
  ];

  const moreItems: SidebarNavItem[] = [
    { key: 'profile', icon: 'user-circle', label: 'My profile', onClick: () => attempt(() => navigate('/host/profile')) },
    { key: 'availability', icon: 'calendar', label: 'Availability', onClick: () => attempt(() => navigate('/host/availability')) },
    { key: 'past', icon: 'archive', label: 'Past trips', onClick: () => attempt(() => navigate('/host/past')) },
    { key: 'faq', icon: 'help-circle', label: 'FAQ', onClick: () => setShowFaq(true) },
    { key: 'support', icon: 'headset', label: 'Support', onClick: () => setShowSupport(true) },
    { key: 'notifications', icon: 'bell', label: 'Notifications', onClick: () => setShowNotifications((v) => !v) },
    { key: 'feedback', icon: 'message-2', label: 'Feedback', onClick: () => setShowFeedback((v) => !v) },
  ];

  return (
    <>
      <KeyboardCollapse className="md:hidden">
        <div ref={anchorRef}>
          <FloatingTabBar items={tabItems} active={active} />
        </div>
      </KeyboardCollapse>

      {moreOpen && (
        <BottomSheet title="More" onClose={() => setMoreOpen(false)}>
          <div className="flex flex-col gap-0.5">
            {moreItems.map((item) => (
              <NavItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                active={item.key === 'profile' && pathname.startsWith('/host/profile')}
                onClick={() => { item.onClick?.(); setMoreOpen(false); }}
              />
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', margin: '10px 8px' }} />
          <div className="px-2 pb-1">
            <AccountMenu variant="quiet" onNavigate={() => setMoreOpen(false)} />
          </div>
        </BottomSheet>
      )}

      {showFaq && <FaqModal audience="host" onClose={() => setShowFaq(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
      {showFeedback && <FeedbackPopover anchorRef={anchorRef} onClose={() => setShowFeedback(false)} />}
      {showNotifications && <NotificationsPopover anchorRef={anchorRef} onClose={() => setShowNotifications(false)} />}
    </>
  );
}

export default HostTabBar;
